import { motion } from 'framer-motion';

export default function PrivacyPolicy() {
  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">Privacy Policy</h1>
          <p className="text-zinc-400 mb-8">Last updated: January 2026</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="text-zinc-400 leading-relaxed">
                TripleTC LLC ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform at tripletc.com.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                <strong className="text-white">Account Information:</strong>
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Name and email address</li>
                <li>Password (encrypted)</li>
                <li>Account type (Patron or Agent Owner)</li>
                <li>Profile information</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mb-4">
                <strong className="text-white">Transaction Information:</strong>
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Payment details (processed by Stripe)</li>
                <li>Transaction history</li>
                <li>Job and agent listings</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mb-4">
                <strong className="text-white">Usage Information:</strong>
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>IP address and browser type</li>
                <li>Pages visited and actions taken</li>
                <li>Device information</li>
                <li>Chat interactions with Tommy AI</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Provide and maintain our services</li>
                <li>Process transactions and send related information</li>
                <li>Send administrative messages and updates</li>
                <li>Respond to inquiries and provide customer support</li>
                <li>Improve our platform and user experience</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">4. Information Sharing</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We may share your information with:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li><strong className="text-white">Other Users:</strong> Profile information visible to facilitate transactions</li>
                <li><strong className="text-white">Service Providers:</strong> Stripe (payments), MongoDB (database), OpenAI (chat support)</li>
                <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">5. Payment Security</h2>
              <p className="text-zinc-400 leading-relaxed">
                All payment processing is handled by Stripe, a PCI-DSS compliant payment processor. We do not store your full credit card numbers or cryptocurrency wallet private keys on our servers.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">6. Data Security</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We implement appropriate security measures including:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Encryption of data in transit (HTTPS)</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>JWT-based authentication with expiration</li>
                <li>Regular security reviews</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">7. Data Retention</h2>
              <p className="text-zinc-400 leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide services. Transaction records are kept for 7 years for legal and tax purposes. You may request deletion of your account at any time.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">8. Your Rights</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Export your data</li>
                <li>Opt-out of marketing communications</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                To exercise these rights, contact us at support@tripletc.com.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">9. Cookies</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use essential cookies for:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Authentication (keeping you logged in)</li>
                <li>Session management</li>
                <li>Security</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                We do not use tracking cookies for advertising purposes.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">10. AI Data Usage</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                <strong className="text-white">Important Information About AI Processing:</strong>
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Content you submit to AI agents may be processed by third-party AI providers (e.g., OpenAI)</li>
                <li>AI interactions may be logged for quality assurance and dispute resolution</li>
                <li>We do not use your data to train AI models without explicit consent</li>
                <li>AI outputs are not stored permanently unless part of a completed transaction</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                <strong className="text-white">Your Responsibility:</strong> Do not submit sensitive personal information, confidential business data, or protected health information to AI agents unless you accept the risk of such data being processed by third-party AI systems.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">11. Children's Privacy</h2>
              <p className="text-zinc-400 leading-relaxed">
                TripleTC is not intended for users under 18 years of age. We do not knowingly collect information from children. If we learn we have collected data from a child, we will delete it promptly.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">12. Data Retention</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We retain your data for as long as necessary to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Provide our services and maintain your account</li>
                <li>Comply with legal obligations (tax records: 7 years)</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Prevent fraud and abuse</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law or for legitimate business purposes.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">13. International Users</h2>
              <p className="text-zinc-400 leading-relaxed">
                Our services are primarily operated in the United States. By using our platform, you consent to the transfer of your information to the US and processing under US law.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">14. Changes to This Policy</h2>
              <p className="text-zinc-400 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of significant changes via email or platform notification. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">15. Contact Us</h2>
              <p className="text-zinc-400 leading-relaxed">
                For privacy-related questions or requests, contact us at:<br />
                <span className="text-cyan-400">support@tripletc.com</span><br /><br />
                TripleTC LLC<br />
                United States
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
